'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PermutationList, PermutationListItem } from '@/lib/types';
import api from '@/lib/api';
import { useConfirm } from './ConfirmDialog';
import { useToast } from './Toast';
import FeedbackButtons from './FeedbackButtons';

interface UnifiedListModalProps {
  initialMode?: 'picker' | 'manager';
  initialListId?: number;
  onSelect?: (itemValue: string) => void;
  onInsertVariable?: (listName: string) => void;
  onClose: () => void;
}

export default function UnifiedListModal({
  initialMode = 'manager',
  initialListId,
  onSelect,
  onInsertVariable,
  onClose,
}: UnifiedListModalProps) {
  // Mode state (can be toggled)
  const [mode, setMode] = useState<'picker' | 'manager'>(initialMode);

  // Lists state
  const [lists, setLists] = useState<PermutationList[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(initialListId || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [isCreating, setIsCreating] = useState(false);
  const [detailIndex, setDetailIndex] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);

  // Create list form
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListPrompt, setNewListPrompt] = useState('{value}');

  // Edit list state
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescriptionValue, setEditDescriptionValue] = useState('');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editPromptValue, setEditPromptValue] = useState('');

  // Edit item state
  const [editingItemValue, setEditingItemValue] = useState(false);
  const [editItemValueText, setEditItemValueText] = useState('');

  // Add item state
  const [newItemValue, setNewItemValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');

  // Sidebar search state
  const [listSearch, setListSearch] = useState('');

  // Preview generation state
  const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);
  const [previewProgress, setPreviewProgress] = useState<{ total: number; completed: number; current: string } | null>(null);
  const [previewCacheBuster, setPreviewCacheBuster] = useState<number>(Date.now());
  const [regeneratingItemId, setRegeneratingItemId] = useState<number | null>(null);

  // Multi-select state (for picker mode)
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());

  const { confirm } = useConfirm();
  const toast = useToast();

  // Get selected list
  const selectedList = useMemo(() =>
    lists.find(l => l.id === selectedListId) || null,
    [lists, selectedListId]
  );

  // Get items for current list
  const items = selectedList?.items || [];

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const searchLower = search.toLowerCase();
    return items.filter((item) =>
      item.value.toLowerCase().includes(searchLower)
    );
  }, [items, search]);

  // Current item in detail view
  const currentItem = detailIndex !== null ? filteredItems[detailIndex] : null;

  // Helper to get image URL with cache buster
  const getPreviewUrl = (previewImage: string) => {
    return `${api.getImageUrl(previewImage)}?t=${previewCacheBuster}`;
  };

  // Fetch lists (preserves current selection, only used for refreshing data)
  const fetchLists = useCallback(async (isInitialLoad = false) => {
    try {
      setIsLoading(true);
      const data = await api.getPermutationLists();
      setLists(data);

      // Only set selection on initial load
      if (isInitialLoad) {
        // Set initial list if provided
        if (initialListId && data.find(l => l.id === initialListId)) {
          setSelectedListId(initialListId);
        }
        // Try to restore last viewed list from localStorage
        else if (!initialListId && data.length > 0) {
          const lastViewedId = localStorage.getItem('lastViewedListId');
          if (lastViewedId) {
            const lastViewedIdNum = parseInt(lastViewedId, 10);
            if (data.find(l => l.id === lastViewedIdNum)) {
              setSelectedListId(lastViewedIdNum);
              return;
            }
          }
          // Fallback to first list in manager mode
          if (mode === 'manager') {
            setSelectedListId(data[0].id);
          }
        }
      }
    } catch (err) {
      setError('Failed to load lists');
    } finally {
      setIsLoading(false);
    }
  }, [initialListId, mode]);

  useEffect(() => {
    fetchLists(true); // Initial load - set selection
  }, []);

  // Focus search input
  useEffect(() => {
    if (detailIndex === null && !isCreating) {
      searchInputRef.current?.focus();
    }
  }, [detailIndex, isCreating, selectedListId]);

  // Clear multi-select when list changes
  useEffect(() => {
    setSelectedItemIds(new Set());
  }, [selectedListId]);

  // Handle list change
  const handleListChange = (listId: number) => {
    setSelectedListId(listId);
    setSearch('');
    setDetailIndex(null);
    setIsCreating(false);
    setShowFullDescription(false);
    // Persist last viewed list
    localStorage.setItem('lastViewedListId', String(listId));
  };

  // Filter lists by search
  const filteredLists = useMemo(() => {
    if (!listSearch.trim()) return lists;
    return lists.filter(l =>
      l.name.toLowerCase().includes(listSearch.toLowerCase())
    );
  }, [lists, listSearch]);

  // Open detail view (save scroll position)
  const openDetailView = (index: number) => {
    if (gridContainerRef.current) {
      savedScrollPosition.current = gridContainerRef.current.scrollTop;
    }
    setDetailIndex(index);
  };

  // Close detail view (restore scroll position)
  const closeDetailView = () => {
    setDetailIndex(null);
    setEditingItemValue(false);
    // Restore scroll position after render
    requestAnimationFrame(() => {
      if (gridContainerRef.current) {
        gridContainerRef.current.scrollTop = savedScrollPosition.current;
      }
    });
  };

  // Navigation in detail view
  const goToPrevious = () => {
    if (detailIndex !== null && detailIndex > 0) {
      setDetailIndex(detailIndex - 1);
      setEditingItemValue(false);
    }
  };

  const goToNext = () => {
    if (detailIndex !== null && detailIndex < filteredItems.length - 1) {
      setDetailIndex(detailIndex + 1);
      setEditingItemValue(false);
    }
  };

  // Select current item
  const handleSelectCurrent = () => {
    if (currentItem && onSelect) {
      onSelect(currentItem.value);
      onClose();
    }
  };

  // Multi-select functions
  const toggleItemSelection = (itemId: number) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedItemIds(new Set());
  };

  const handleMultiSelectInsert = () => {
    if (selectedItemIds.size === 0 || !onSelect) return;

    // Get selected item values in their current order
    const selectedValues = filteredItems
      .filter(item => selectedItemIds.has(item.id))
      .map(item => item.value);

    if (selectedValues.length === 1) {
      // Single item - insert as plain value
      onSelect(selectedValues[0]);
    } else {
      // Multiple items - insert as {value1|value2|value3} syntax
      onSelect(`{${selectedValues.join('|')}}`);
    }
    onClose();
  };

  const toggleMultiSelectMode = () => {
    if (multiSelectMode) {
      // Exiting multi-select mode - clear selection
      clearSelection();
    }
    setMultiSelectMode(!multiSelectMode);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip keyboard shortcuts when editing item value
      if (editingItemValue) {
        if (e.key === 'Escape') {
          setEditingItemValue(false);
        }
        return;
      }

      if (e.key === 'Escape') {
        if (detailIndex !== null) {
          closeDetailView();
        } else if (multiSelectMode) {
          // Exit multi-select mode on Escape
          toggleMultiSelectMode();
        } else if (isCreating) {
          setIsCreating(false);
        } else {
          onClose();
        }
      } else if (e.key === 'Enter' && multiSelectMode && selectedItemIds.size > 0) {
        // Insert selected items in multi-select mode
        e.preventDefault();
        handleMultiSelectInsert();
      } else if (detailIndex !== null) {
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          goToPrevious();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          goToNext();
        } else if (e.key === 'Enter' && onSelect) {
          e.preventDefault();
          handleSelectCurrent();
        } else if ((e.key === 'Delete' || e.key === 'Backspace') && mode === 'manager') {
          e.preventDefault();
          handleDeleteFromDetail();
        } else if (e.key === ' ' && mode === 'manager') {
          e.preventDefault();
          handleToggleFromDetail();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, detailIndex, filteredItems, mode, editingItemValue, multiSelectMode, selectedItemIds]);

  // === LIST MANAGEMENT ===

  const handleCreateList = async () => {
    if (!newListName.trim()) return;

    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(newListName)) {
      setError('Name must start with letter/underscore and contain only letters, numbers, underscores');
      return;
    }

    try {
      const newList = await api.createPermutationList({
        name: newListName.trim(),
        description: newListDescription.trim() || undefined,
        prompt: newListPrompt.trim() || undefined,
      });
      setLists([...lists, newList]);
      setSelectedListId(newList.id);
      setIsCreating(false);
      setNewListName('');
      setNewListDescription('');
      setNewListPrompt('{value}');
      setError(null);
      toast.success('List created');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleDeleteList = async () => {
    if (!selectedList) return;

    const confirmed = await confirm({
      title: 'Delete List',
      message: 'This will permanently delete this list and all its items. This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.deletePermutationList(selectedList.id);
      const newLists = lists.filter(l => l.id !== selectedList.id);
      setLists(newLists);
      setSelectedListId(newLists.length > 0 ? newLists[0].id : null);
      toast.success('List deleted');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleSaveDescription = async () => {
    if (!selectedList) return;

    try {
      const updated = await api.updatePermutationList(selectedList.id, {
        description: editDescriptionValue.trim() || undefined,
      });
      setLists(lists.map(l => l.id === updated.id ? updated : l));
      setEditingDescription(false);
      toast.success('Description updated');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleSavePrompt = async () => {
    if (!selectedList) return;

    try {
      const updated = await api.updatePermutationList(selectedList.id, {
        prompt: editPromptValue.trim() || undefined,
      });
      setLists(lists.map(l => l.id === updated.id ? updated : l));
      setEditingPrompt(false);
      toast.success('Prompt updated');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // === ITEM MANAGEMENT ===

  const handleAddItem = async () => {
    if (!selectedList || !newItemValue.trim()) return;

    try {
      const item = await api.addListItem(selectedList.id, {
        value: newItemValue.trim(),
        is_active: true,
      });
      const updated = {
        ...selectedList,
        items: [...selectedList.items, item],
        active_count: selectedList.active_count + 1
      };
      setLists(lists.map(l => l.id === updated.id ? updated : l));
      setNewItemValue('');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleToggleItem = async (item: PermutationListItem) => {
    if (!selectedList) return;

    try {
      await api.updateListItem(selectedList.id, item.id, {
        is_active: !item.is_active,
      });
      const updatedItems = selectedList.items.map(i =>
        i.id === item.id ? { ...i, is_active: !i.is_active } : i
      );
      const updated = {
        ...selectedList,
        items: updatedItems,
        active_count: updatedItems.filter(i => i.is_active).length
      };
      setLists(lists.map(l => l.id === updated.id ? updated : l));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!selectedList) return;

    try {
      await api.deleteListItem(selectedList.id, itemId);
      const updatedItems = selectedList.items.filter(i => i.id !== itemId);
      const updated = {
        ...selectedList,
        items: updatedItems,
        active_count: updatedItems.filter(i => i.is_active).length
      };
      setLists(lists.map(l => l.id === updated.id ? updated : l));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleToggleFromDetail = async () => {
    if (!currentItem) return;
    await handleToggleItem(currentItem);
  };

  const handleDeleteFromDetail = async () => {
    if (!selectedList || !currentItem || detailIndex === null) return;

    try {
      await api.deleteListItem(selectedList.id, currentItem.id);
      const updatedItems = selectedList.items.filter(i => i.id !== currentItem.id);
      const updated = {
        ...selectedList,
        items: updatedItems,
        active_count: updatedItems.filter(i => i.is_active).length
      };
      setLists(lists.map(l => l.id === updated.id ? updated : l));

      // Navigate to next/previous item
      const newFiltered = updatedItems.filter(item =>
        !search.trim() || item.value.toLowerCase().includes(search.toLowerCase())
      );

      if (newFiltered.length === 0) {
        closeDetailView();
      } else if (detailIndex >= newFiltered.length) {
        setDetailIndex(newFiltered.length - 1);
      }

      toast.success('Item deleted');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleRegeneratePreview = async () => {
    if (!selectedList || !currentItem) return;

    try {
      setRegeneratingItemId(currentItem.id);
      const result = await api.regenerateItemPreview(selectedList.id, currentItem.id);

      // Update local state with new preview image
      const updatedItems = selectedList.items.map(i =>
        i.id === currentItem.id ? { ...i, preview_image: result.preview_image } : i
      );
      const updated = {
        ...selectedList,
        items: updatedItems,
      };
      setLists(lists.map(l => l.id === updated.id ? updated : l));

      // Bust cache to force image reload
      setPreviewCacheBuster(Date.now());
      toast.success('Preview regenerated');
    } catch (err) {
      toast.error((err as Error).message || 'Failed to regenerate preview');
    } finally {
      setRegeneratingItemId(null);
    }
  };

  const handleEditItemValue = async () => {
    if (!selectedList || !currentItem || !editItemValueText.trim()) return;

    const newValue = editItemValueText.trim();
    // Only update if value actually changed
    if (newValue === currentItem.value) {
      setEditingItemValue(false);
      return;
    }

    try {
      // Update value and clear preview image since value changed
      await api.updateListItem(selectedList.id, currentItem.id, {
        value: newValue,
        preview_image: null, // Clear preview so it can be regenerated
      });

      // Update local state
      const updatedItems = selectedList.items.map(i =>
        i.id === currentItem.id ? { ...i, value: newValue, preview_image: null } : i
      );
      const updated = {
        ...selectedList,
        items: updatedItems,
      };
      setLists(lists.map(l => l.id === updated.id ? updated : l));
      setEditingItemValue(false);
      toast.success('Item updated (preview cleared for regeneration)');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleAIGenerate = async () => {
    if (!selectedList) return;

    setIsGenerating(true);
    try {
      const existingItems = selectedList.items.map(item => item.value);
      const response = await api.generateListItems({
        list_name: selectedList.name,
        description: selectedList.description || undefined,
        instructions: aiInstructions.trim() || undefined,
        count: 10,
        existing_items: existingItems,
      });

      let updatedList = selectedList;
      for (const value of response.items) {
        const item = await api.addListItem(selectedList.id, {
          value: value.trim(),
          is_active: true,
        });
        updatedList = {
          ...updatedList,
          items: [...updatedList.items, item],
          active_count: updatedList.active_count + 1
        };
      }

      setLists(lists.map(l => l.id === updatedList.id ? updatedList : l));
      toast.success(`Added ${response.items.length} AI-generated items`);
    } catch (err) {
      const message = (err as Error).message;
      if (message.includes('Cannot connect')) {
        setError('Local LLM not reachable. Check the llama-server configured via LM_STUDIO_URL.');
      } else {
        setError(message);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // === PREVIEW GENERATION ===

  const handleGeneratePreviews = async (missingOnly: boolean = false) => {
    if (!selectedList) return;

    if (!selectedList.prompt) {
      setError('Please set a prompt template first. Use {value} as a placeholder.');
      return;
    }

    setIsGeneratingPreviews(true);
    setPreviewProgress({ total: selectedList.items.length, completed: 0, current: '' });

    try {
      const result = await api.generateListPreviews(selectedList.id, missingOnly);
      toast.success(result.message || 'Preview generation started');

      const pollInterval = setInterval(async () => {
        try {
          const status = await api.getPreviewGenerationStatus(selectedList.id);

          if (status.status === 'in_progress') {
            setPreviewProgress({
              total: status.total || 0,
              completed: status.completed || 0,
              current: status.current || '',
            });
          } else if (status.status === 'completed' || status.status === 'idle') {
            clearInterval(pollInterval);
            setIsGeneratingPreviews(false);
            setPreviewProgress(null);
            await fetchLists();
            setPreviewCacheBuster(Date.now());
            toast.success('Preview generation completed');
          } else if (status.status === 'cancelled') {
            clearInterval(pollInterval);
            setIsGeneratingPreviews(false);
            setPreviewProgress(null);
            await fetchLists();
            setPreviewCacheBuster(Date.now());
            toast.success('Preview generation cancelled');
          } else if (status.status === 'error') {
            clearInterval(pollInterval);
            setIsGeneratingPreviews(false);
            setPreviewProgress(null);
            setError(status.message || 'Preview generation failed');
          }
        } catch (err) {
          clearInterval(pollInterval);
          setIsGeneratingPreviews(false);
          setPreviewProgress(null);
        }
      }, 2000);
    } catch (err) {
      setIsGeneratingPreviews(false);
      setPreviewProgress(null);
      setError((err as Error).message);
    }
  };

  const handleCancelPreviews = async () => {
    if (!selectedList) return;
    try {
      await api.cancelListPreviews(selectedList.id);
      toast.success('Cancellation requested');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // === DETAIL VIEW ===
  if (currentItem && detailIndex !== null) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={closeDetailView}
      >
        <div
          className="bg-[var(--bg-elevated)] rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden border border-[var(--border-subtle)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Up Arrow */}
          <button
            onClick={goToPrevious}
            disabled={detailIndex === 0}
            className={`
              relative z-10 flex items-center justify-center py-5 border-b border-[var(--border-subtle)]
              transition-colors cursor-pointer min-h-[64px]
              ${detailIndex === 0
                ? 'text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-primary)]'}
            `}
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
            <span className="ml-3 text-sm font-medium">Previous ({detailIndex}/{filteredItems.length})</span>
          </button>

          {/* Image Preview */}
          <div className="relative aspect-square bg-[var(--bg-primary)] flex items-center justify-center overflow-hidden">
            {currentItem.preview_image ? (
              <img
                src={getPreviewUrl(currentItem.preview_image)}
                alt={currentItem.value}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center text-[var(--text-muted)]">
                <svg className="w-16 h-16 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="mt-2 text-sm">No preview</span>
              </div>
            )}

            {/* Active/Inactive badge */}
            <div className={`absolute top-3 left-3 px-2 py-1 rounded text-xs font-medium ${
              currentItem.is_active
                ? 'bg-green-500/20 text-green-400'
                : 'bg-red-500/20 text-red-400'
            }`}>
              {currentItem.is_active ? 'Active' : 'Inactive'}
            </div>

            {/* Regenerate preview button */}
            {selectedList?.prompt && (
              <button
                onClick={handleRegeneratePreview}
                disabled={regeneratingItemId === currentItem.id}
                className={`absolute top-3 right-3 p-2 rounded-lg transition-colors cursor-pointer ${
                  regeneratingItemId === currentItem.id
                    ? 'bg-[var(--bg-elevated)]/80 text-[var(--text-muted)]'
                    : 'bg-[var(--bg-elevated)]/80 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--accent)]'
                }`}
                title="Regenerate preview"
              >
                <svg
                  className={`w-5 h-5 ${regeneratingItemId === currentItem.id ? 'animate-spin' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Item Value & Actions */}
          <div className="px-6 py-4 border-t border-[var(--border-subtle)]">
            {/* Editable Item Value */}
            {editingItemValue ? (
              <div className="mb-4 space-y-2">
                <textarea
                  value={editItemValueText}
                  onChange={(e) => setEditItemValueText(e.target.value)}
                  onKeyDown={(e) => {
                    // Cmd/Ctrl+Enter saves; plain Enter inserts a newline so
                    // long, multi-line prompts can be edited comfortably.
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleEditItemValue();
                    }
                    if (e.key === 'Escape') setEditingItemValue(false);
                  }}
                  rows={4}
                  className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-y min-h-[80px] text-sm leading-relaxed"
                  autoFocus
                />
                <div className="flex justify-center gap-2">
                  <button
                    onClick={handleEditItemValue}
                    className="px-3 py-1 bg-[var(--accent)] text-black rounded text-sm font-medium hover:bg-[var(--accent-hover)] transition-colors cursor-pointer"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingItemValue(false)}
                    className="px-3 py-1 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm hover:bg-[var(--bg-elevated)] transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-[var(--text-muted)] text-center">
                  ⌘/Ctrl+Enter to save · Esc to cancel · editing clears the preview image
                </p>
              </div>
            ) : (
              <div className="text-center mb-4">
                <p className="text-lg font-medium">{currentItem.value}</p>
                {mode === 'manager' && (
                  <button
                    onClick={() => {
                      setEditItemValueText(currentItem.value);
                      setEditingItemValue(true);
                    }}
                    className="text-xs text-[var(--accent)] hover:underline cursor-pointer mt-1"
                  >
                    Edit
                  </button>
                )}
              </div>
            )}

            {/* Feedback buttons for preview images */}
            {currentItem.preview_image && selectedList && (
              <div className="flex justify-center mb-4">
                <FeedbackButtons
                  imagePath={currentItem.preview_image}
                  imageType="preview"
                  listId={selectedList.id}
                  listItemId={currentItem.id}
                  size="md"
                />
              </div>
            )}

            <div className="flex gap-2">
              {/* Back button */}
              <button
                onClick={closeDetailView}
                className="flex-1 px-4 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg hover:bg-[var(--bg-elevated)] transition-colors cursor-pointer text-sm"
              >
                Back to Grid
              </button>

              {/* Toggle button (manager mode) */}
              {mode === 'manager' && (
                <button
                  onClick={handleToggleFromDetail}
                  className={`px-4 py-2 rounded-lg transition-colors cursor-pointer text-sm font-medium ${
                    currentItem.is_active
                      ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  }`}
                >
                  {currentItem.is_active ? 'Deactivate' : 'Activate'}
                </button>
              )}

              {/* Delete button (manager mode) */}
              {mode === 'manager' && (
                <button
                  onClick={handleDeleteFromDetail}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors cursor-pointer text-sm font-medium"
                >
                  Delete
                </button>
              )}

              {/* Select button (picker mode or always) */}
              {onSelect && (
                <button
                  onClick={handleSelectCurrent}
                  className="flex-1 px-4 py-2 bg-[var(--accent)] text-black font-medium rounded-lg hover:bg-[var(--accent-hover)] transition-colors cursor-pointer text-sm"
                >
                  Use This
                </button>
              )}
            </div>
          </div>

          {/* Down Arrow */}
          <button
            onClick={goToNext}
            disabled={detailIndex === filteredItems.length - 1}
            className={`
              relative z-10 flex items-center justify-center py-5 border-t border-[var(--border-subtle)]
              transition-colors cursor-pointer min-h-[64px]
              ${detailIndex === filteredItems.length - 1
                ? 'text-[var(--text-muted)] opacity-30 cursor-not-allowed'
                : 'text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-primary)]'}
            `}
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <span className="ml-3 text-sm font-medium">Next ({detailIndex + 2}/{filteredItems.length})</span>
          </button>

          {/* Keyboard hints */}
          <div className="px-4 py-2 bg-[var(--bg-primary)] text-xs text-[var(--text-muted)] text-center border-t border-[var(--border-subtle)]">
            ↑↓ Navigate • Enter Select{mode === 'manager' ? ' • Space Toggle • Del Delete' : ''} • Esc Back
          </div>
        </div>
      </div>
    );
  }

  // === MAIN GRID VIEW ===
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-surface)] rounded-xl w-full max-w-7xl h-[90vh] overflow-hidden border border-[var(--border-subtle)] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-16 border-b border-[var(--border-subtle)] flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold">
              {mode === 'picker' && selectedList ? `Select from "${selectedList.name}"` : 'Permutation Lists'}
            </h2>
            <p className="text-sm text-[var(--text-muted)]">
              {mode === 'picker'
                ? `${filteredItems.length} item${filteredItems.length !== 1 ? 's' : ''} available`
                : "Browse and manage your item collections"
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Mode Toggle */}
            <button
              onClick={() => setMode(mode === 'picker' ? 'manager' : 'picker')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                mode === 'manager'
                  ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
              title={mode === 'picker' ? 'Switch to edit mode' : 'Switch to browse mode'}
            >
              {mode === 'manager' ? (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Edit Mode
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Browse
                </>
              )}
            </button>
            {/* Multi-Select Toggle (picker mode only, when a list is selected) */}
            {mode === 'picker' && selectedList && onSelect && (
              <button
                onClick={toggleMultiSelectMode}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  multiSelectMode
                    ? 'bg-[var(--accent)] text-black'
                    : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
                title={multiSelectMode ? 'Exit multi-select mode' : 'Select multiple items'}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Multi
              </button>
            )}
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors cursor-pointer"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-72 border-r border-[var(--border-subtle)] flex flex-col flex-shrink-0">
            {/* New List button (manager mode) */}
            {mode === 'manager' && (
              <div className="p-4 border-b border-[var(--border-subtle)]">
                <button
                  onClick={() => { setIsCreating(true); setSelectedListId(null); }}
                  className="btn btn-primary w-full"
                >
                  + New List
                </button>
              </div>
            )}

            {/* List Search */}
            <div className="p-2 border-b border-[var(--border-subtle)]">
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-muted)]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  placeholder="Filter lists..."
                  className="w-full pl-8 pr-2 py-1.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none text-sm"
                />
              </div>
            </div>

            {/* Lists */}
            <div className="flex-1 overflow-y-auto p-2">
              {isLoading ? (
                <div className="flex items-center justify-center h-20 text-[var(--text-muted)]">
                  Loading...
                </div>
              ) : filteredLists.length === 0 ? (
                <div className="flex items-center justify-center h-20 text-sm text-[var(--text-muted)]">
                  {lists.length === 0 ? 'No lists yet' : 'No matches'}
                </div>
              ) : (
                filteredLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => handleListChange(list.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 transition-colors cursor-pointer ${
                      selectedListId === list.id
                        ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
                        : 'hover:bg-[var(--bg-hover)] text-[var(--text-secondary)]'
                    }`}
                  >
                    <div className="font-medium truncate">{list.name}</div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {list.active_count} of {list.items.length} active
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {isCreating ? (
              /* Create New List Form */
              <div className="p-6 overflow-y-auto">
                <h3 className="text-lg font-medium mb-4">Create New List</h3>
                <div className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">
                      Name (used as {'{name}'} in prompts)
                    </label>
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
                      className="input"
                      placeholder="e.g., backgrounds"
                      autoFocus
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Letters, numbers, underscores only. Must start with letter or underscore.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">
                      Description (optional, used for AI generation)
                    </label>
                    <textarea
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      className="input resize-none"
                      rows={3}
                      placeholder="e.g., Scene background locations for fantasy art"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">
                      Preview Prompt Template
                    </label>
                    <textarea
                      value={newListPrompt}
                      onChange={(e) => setNewListPrompt(e.target.value)}
                      className="input resize-none"
                      rows={3}
                      placeholder="e.g., A beautiful {value}, high quality photo"
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Use {'{value}'} as placeholder. Used to generate 512x512 preview images.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleCreateList} className="btn btn-primary">
                      Create List
                    </button>
                    <button
                      onClick={() => { setIsCreating(false); setNewListName(''); setNewListDescription(''); setNewListPrompt('{value}'); }}
                      className="btn btn-ghost"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedList ? (
              <>
                {/* List Header (manager mode) */}
                {mode === 'manager' && (
                  <div className="p-4 border-b border-[var(--border-subtle)] space-y-4">
                    {/* List name and actions */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium">{selectedList.name}</h3>
                        {editingDescription ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              value={editDescriptionValue}
                              onChange={(e) => setEditDescriptionValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSaveDescription();
                                if (e.key === 'Escape') setEditingDescription(false);
                              }}
                              className="input text-sm resize-y w-full"
                              rows={5}
                              placeholder="Description (used for AI generation)"
                              autoFocus
                            />
                            <div className="flex items-center gap-2">
                              <button onClick={handleSaveDescription} className="btn btn-primary text-xs py-1 px-3">Save</button>
                              <button onClick={() => setEditingDescription(false)} className="btn btn-ghost text-xs py-1 px-3">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-[var(--text-muted)] mt-1">
                            {selectedList.description ? (
                              <>
                                <span>
                                  {showFullDescription || selectedList.description.length <= 200
                                    ? selectedList.description
                                    : `${selectedList.description.slice(0, 200)}...`}
                                </span>
                                {selectedList.description.length > 200 && (
                                  <button
                                    onClick={() => setShowFullDescription(!showFullDescription)}
                                    className="text-xs text-[var(--accent)] hover:underline cursor-pointer ml-1"
                                  >
                                    {showFullDescription ? 'show less' : 'show all'}
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="italic">No description</span>
                            )}
                            <button
                              onClick={() => { setEditingDescription(true); setEditDescriptionValue(selectedList.description || ''); setShowFullDescription(false); }}
                              className="text-xs text-[var(--accent)] hover:underline cursor-pointer ml-2"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {onInsertVariable && (
                          <button
                            onClick={() => { onInsertVariable(selectedList.name); onClose(); }}
                            className="btn btn-ghost text-[var(--accent)] text-sm"
                          >
                            Insert {`{${selectedList.name}}`}
                          </button>
                        )}
                        <button
                          onClick={handleDeleteList}
                          className="btn btn-ghost text-[var(--error)] hover:bg-[var(--error-soft)] text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Prompt Template */}
                    <div className="p-3 bg-[var(--bg-elevated)] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-[var(--text-secondary)]">
                          Preview Prompt Template
                        </label>
                        <div className="flex gap-2">
                          {!editingPrompt && (
                            <button
                              onClick={() => { setEditingPrompt(true); setEditPromptValue(selectedList.prompt || ''); }}
                              className="text-xs text-[var(--accent)] hover:underline cursor-pointer"
                            >
                              {selectedList.prompt ? 'Edit' : 'Set Prompt'}
                            </button>
                          )}
                          {selectedList.prompt && !editingPrompt && !isGeneratingPreviews && (
                            <>
                              <button
                                onClick={() => handleGeneratePreviews(true)}
                                className="text-xs text-[var(--accent)] hover:underline cursor-pointer"
                                title="Generate previews only for items that don't have one"
                              >
                                Generate Missing
                              </button>
                              <span className="text-xs text-[var(--text-muted)]">|</span>
                              <button
                                onClick={() => handleGeneratePreviews(false)}
                                className="text-xs text-[var(--accent)] hover:underline cursor-pointer"
                                title="Regenerate all preview images"
                              >
                                Refresh All
                              </button>
                            </>
                          )}
                          {isGeneratingPreviews && (
                            <button
                              onClick={handleCancelPreviews}
                              className="text-xs text-red-400 hover:underline cursor-pointer"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                      {editingPrompt ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editPromptValue}
                            onChange={(e) => setEditPromptValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSavePrompt()}
                            className="input text-sm"
                            placeholder="e.g., A beautiful {value}, high quality photo"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button onClick={handleSavePrompt} className="btn btn-primary text-xs py-1 px-2">Save</button>
                            <button onClick={() => setEditingPrompt(false)} className="btn btn-ghost text-xs py-1 px-2">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-[var(--text-muted)]">
                          {selectedList.prompt || 'No prompt set. Set a prompt to generate preview images.'}
                        </p>
                      )}
                      {isGeneratingPreviews && previewProgress && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1">
                            <span>Generating: {previewProgress.current || '...'}</span>
                            <span>{previewProgress.completed} / {previewProgress.total}</span>
                          </div>
                          <div className="w-full h-1.5 bg-[var(--bg-surface)] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[var(--accent)] transition-all duration-300"
                              style={{ width: `${(previewProgress.completed / previewProgress.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Add item */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newItemValue}
                        onChange={(e) => setNewItemValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                        className="input flex-1"
                        placeholder="Add new item..."
                      />
                      <button onClick={handleAddItem} className="btn btn-primary">
                        Add
                      </button>
                    </div>

                    {/* AI Generate */}
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={aiInstructions}
                        onChange={(e) => setAiInstructions(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAIGenerate()}
                        className="input flex-1 text-sm"
                        placeholder="AI instructions (e.g., 'focus on nature themes', 'modern style only')..."
                      />
                      <button
                        onClick={handleAIGenerate}
                        disabled={isGenerating}
                        className="btn btn-ghost flex items-center gap-1.5"
                        title="Generate items with AI (LM Studio)"
                      >
                        {isGenerating ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                          </svg>
                        )}
                        <span className="hidden sm:inline">{isGenerating ? 'Generating...' : 'AI Generate'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Search */}
                <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                  <div className="relative">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search items..."
                      className="w-full pl-10 pr-4 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none text-sm"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Items Grid */}
                <div ref={gridContainerRef} className="flex-1 overflow-y-auto p-4">
                  {filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-[var(--text-muted)]">
                      <svg className="w-12 h-12 mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p>{items.length === 0 ? 'No items yet' : 'No items found'}</p>
                      {search && (
                        <button
                          onClick={() => setSearch('')}
                          className="mt-2 text-sm text-[var(--accent)] hover:underline cursor-pointer"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {filteredItems.map((item, index) => {
                        const isSelected = selectedItemIds.has(item.id);
                        return (
                          /* Search link is a sibling: an <a> inside a <button> is invalid */
                          <div key={item.id} className="group relative">
                          {!multiSelectMode && (
                            <SearchPromptsButton
                              text={item.value}
                              className="absolute top-2 right-2 z-20 p-1.5 rounded-lg bg-black/60 text-white/80 hover:bg-[var(--accent)] hover:text-black opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                            />
                          )}
                          <button
                            onClick={() => multiSelectMode ? toggleItemSelection(item.id) : openDetailView(index)}
                            className={`w-full relative flex flex-col rounded-lg overflow-hidden border-2 transition-all cursor-pointer text-left ${
                              multiSelectMode && isSelected
                                ? 'bg-[var(--accent-soft)] border-[var(--accent)] ring-2 ring-[var(--accent)] ring-opacity-50'
                                : item.is_active
                                  ? 'bg-[var(--bg-primary)] border-[var(--border-subtle)] hover:border-[var(--accent)]'
                                  : 'bg-[var(--bg-surface)] border-[var(--border-subtle)] opacity-60 hover:opacity-80'
                            }`}
                          >
                            {/* Preview Image */}
                            <div className="aspect-square bg-[var(--bg-elevated)] flex items-center justify-center overflow-hidden relative">
                              {item.preview_image ? (
                                <img
                                  src={getPreviewUrl(item.preview_image)}
                                  alt={item.value}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                  loading="lazy"
                                />
                              ) : (
                                <svg className="w-8 h-8 text-[var(--text-muted)] opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              )}

                              {/* Multi-select checkbox indicator */}
                              {multiSelectMode && (
                                <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                  isSelected
                                    ? 'bg-[var(--accent)] text-black'
                                    : 'bg-black/50 text-white border-2 border-white/50'
                                }`}>
                                  {isSelected ? (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  ) : (
                                    <span className="text-xs font-bold">{index + 1}</span>
                                  )}
                                </div>
                              )}

                              {/* Active/Inactive indicator */}
                              {!item.is_active && !multiSelectMode && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <span className="text-xs font-medium text-white/80 bg-black/50 px-2 py-1 rounded">
                                    Inactive
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Item Value */}
                            <div className={`px-2 py-2 text-sm truncate transition-colors ${
                              isSelected ? 'text-[var(--accent)] font-medium' : 'group-hover:text-[var(--accent)]'
                            }`} title={item.value}>
                              {item.value}
                            </div>
                          </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* No list selected */
              <div className="flex-1 flex items-center justify-center text-[var(--text-muted)]">
                <div className="text-center">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
                  </svg>
                  <p>Select a list{mode === 'manager' ? ' or create a new one' : ''}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Multi-select action bar */}
        {multiSelectMode && (
          <div className="px-6 py-3 border-t border-[var(--border-subtle)] bg-[var(--bg-elevated)] flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {selectedItemIds.size === 0 ? (
                  'Click items to select'
                ) : (
                  <>{selectedItemIds.size} item{selectedItemIds.size !== 1 ? 's' : ''} selected</>
                )}
              </span>
              {selectedItemIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] underline cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleMultiSelectMode}
                className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleMultiSelectInsert}
                disabled={selectedItemIds.size === 0}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                  selectedItemIds.size === 0
                    ? 'bg-[var(--bg-hover)] text-[var(--text-muted)] cursor-not-allowed'
                    : 'bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)]'
                }`}
              >
                {selectedItemIds.size <= 1 ? 'Insert' : `Insert {${selectedItemIds.size} items}`}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        {!multiSelectMode && (
          <div className="px-6 py-3 border-t border-[var(--border-subtle)] text-xs text-[var(--text-muted)] text-center">
            Click an item to view details • Esc to close
          </div>
        )}

        {/* Error toast */}
        {error && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-[var(--error-soft)] text-[var(--error)] px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="p-1 hover:bg-[var(--error)] hover:text-white rounded transition-colors cursor-pointer">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
